import { METRIC_BY_ID } from '@repo/types'

import type { AnalyticsMetricId } from './analyticsSolutions'
import type { ROICardDef } from './ROICards'

/**
 * A card built FROM a registry metric, rather than a card that happens to describe
 * one (VRG-459).
 *
 * ## Why this exists, and why it is not applied to the thirteen cards below
 *
 * The three id spaces on this page are disjoint — library KPI tiles (`scans_count`),
 * library ROI cards (`mkt_geo_reach`) and `METRIC_REGISTRY` (`geographic_reach`) —
 * so `METRIC_BY_ID[card.id]` misses on every existing card. `AnalyticsCustomize`
 * documents this at length and reaches the right conclusion: the fix is an AUTHORED
 * `metricId`, decided per card by someone who can say whether the two are the same
 * MEASUREMENT, and it explicitly warns against closing the gap by matching titles.
 * "Counterfeit Deflections" here is a COUNT of incidents; the registry's
 * `counterfeit_deflection_value` is a CURRENCY metric with `contributesToRoi: true`.
 * Fuzzy-matching those would make a count card claim to feed the ROI hero.
 *
 * So this function is used ONLY for cards that ARE a registry metric, created here
 * for that purpose. It reads the title, unit and description straight from the
 * registry rather than restating them, which is what makes the assertion honest: a
 * card whose copy is the metric's own copy cannot be describing a different
 * measurement. The pre-existing cards keep no `metricId` until each is decided.
 *
 * A missing id yields `null` rather than throwing — a registry rename should degrade
 * to one fewer card, not take the analytics page down.
 */
const cardFromMetric = (
  /*
    ⚠️ `AnalyticsMetricId`, not `string` (VERA-260). A card built from a registry metric is
    still a library card, so it owes the same solution classification as a hand-authored
    one — and it can only be held to that if its id is the union `ROICardDef['id']` is.

    This is ALSO why `id` below is now this PARAMETER rather than `metric.id`: the two are
    equal by construction (`METRIC_BY_ID` is keyed by id, which is what the note there
    already said), but `IAnalyticsMetric['id']` is a plain `string`, so reading it back out
    would need a cast — and a cast is exactly the escape hatch that would let an
    unclassified metric become a card. Taking the id from the caller keeps the guarantee
    without one.
  */
  metricId: AnalyticsMetricId,
  term?: string,
): ROICardDef | null => {
  const metric = METRIC_BY_ID[metricId]
  if (!metric) return null

  return {
    // AUTHORED at the call site, exactly as `metricId` is, and for the same reason: this
    // is a FOURTH id space (glossary entry ids), disjoint from the other three, so nothing
    // here can derive it. Deriving one from `metric.id` by de-underscoring would produce
    // `return-fraud-value-recovered`, which is not the entry — and a `term` that does not
    // resolve renders no tooltip at all, silently, because a term-only tooltip has no
    // literal text to fall back to. `glossary-call-sites.guard.spec.ts` catches a written
    // one; it cannot see a computed one.
    term,
    // The card id IS the metric id for these, which is what keeps the two in
    // agreement by construction. The prefixed ids below are historical.
    // Taken from the PARAMETER rather than from `metric.id` — see the note there.
    id: metricId,
    metricId: metric.id,
    title: metric.title,
    // Injected from the ROI response by `useRoiCards`; null is the pre-fetch state.
    value: null,
    unit: metric.unit,
    description: metric.description,
  }
}

/**
 * ⚠️ ONE ENTRY, AND THE OMISSIONS ARE THE DECISION.
 *
 * Three registry metrics declare `inputs` and are therefore candidates for a card
 * that can say `N inputs needed`. Only one of them earns it, and the test is whether
 * supplying the number CHANGES ANYTHING:
 *
 *   return_fraud_value_recovered    HAS a resolver in `ROI_CONTRIBUTION_RESOLVERS`.
 *                                   Supply the annual loss and the card renders a
 *                                   real figure that counts toward the hero. Verified
 *                                   live: em-dash -> `1 input needed` -> $684.93.
 *
 *   counterfeit_deflection_value    NO resolver. Its measured half (invalid scans
 *   new_customer_acquisition_value  priced at product price; a captured lead count)
 *                                   is not produced by `getRoiMetrics`, so supplying
 *                                   every input moves the metric from `needs_input`
 *                                   to `not_computed` — from one em-dash to another.
 *
 * ⚠️ THE SERVER STILL REPORTS THOSE TWO AS `needs_input`, which is a false promise
 * and is reported rather than worked around here. `resolveRoiContributions` tests
 * `missingInputs` BEFORE it looks for a resolver, so a metric that no input can help
 * is nonetheless described as one input away. Its own docstring names the failure
 * exactly — "sends a brand to a form, has them enter a number, and leaves the metric
 * exactly as withheld as before" — and the check order is what produces it. The fix
 * belongs in that function (resolve first, then blame the inputs), not in a client
 * that second-guesses the reason it was given.
 *
 * Until then, giving those two a card would ship precisely the experience this
 * feature exists to prevent. They stay out of the library, so nothing on screen makes
 * the promise. Adding them back is a one-line change once each has a resolver.
 *
 * (Removing `counterfeit_deflection_value` also removes a name collision it created:
 * the library already carries a legacy `legal_counterfeit_deflections` placeholder
 * titled "Counterfeit Deflections", one letter from the registry's "Counterfeit
 * Deflection". They are genuinely different measurements — a COUNT of incidents
 * versus a CURRENCY metric that feeds the ROI hero — which is why the codebase
 * forbids matching the two on title, and why two adjacent rows one letter apart in
 * the card library would have been unreadable.)
 */
const METRIC_CARDS = ([['return_fraud_value_recovered', 'return-fraud-prevented']] as const)
  .map(([metricId, term]) => cardFromMetric(metricId, term))
  .filter((c): c is ROICardDef => c !== null)

const metricCard = (id: AnalyticsMetricId): ROICardDef[] => {
  const card = METRIC_CARDS.find(c => c.id === id)
  return card ? [card] : []
}

/**
 * All available ROI card definitions, organized by tab category.
 *
 * Cards divide into two kinds, and the split is the useful thing to know when reading
 * this file:
 *
 *   REAL      a value computed by `GET /analytics/admin/roi` and injected by
 *             `useRoiCards`. `value: null` here is the pre-fetch state, not a stub.
 *             Today: repeat scan rate, countries reached, invalid scans + rate, leads
 *             captured, time to first scan, reported stolen/lost, items per owner,
 *             resales, average resale price, resales per resold item.
 *
 *   PLACEHOLDER  a card whose inputs do not exist in the system at all. It renders an
 *             em-dash permanently. These are NOT unimplemented queries — the data is
 *             absent, so no amount of backend work in this file's shape resolves them.
 *
 * ⚠️ THREE PLACEHOLDERS HAVE NOW BEEN DELETED AND ONE HAS BECOME REAL. The test is not
 * "is it a placeholder" but "can it ever become real, and is it a duplicate":
 *
 *   DELETED  `mkt_campaign_roi`            needed marketing spend Vera does not hold.
 *   DELETED  `mkt_consumer_touchpoints`    was the Scans KPI tile restated.
 *   DELETED  `mkt_scan_engagement_rate`    needs a UNITS-PRODUCED denominator. A brand's
 *                                          production run happens outside this system,
 *                                          so this is an absent DOMAIN, not a missing
 *                                          column — the same class as Campaign ROI. An
 *                                          earlier revision of this note kept it by
 *                                          misapplying the "one identifiable missing
 *                                          field" test to an input nobody will ever add.
 *   NOW REAL `log_avg_transfer_time`       see below.
 *
 * ⚠️ `log_avg_transfer_time` WAS blocked on a NAMED field and that field now exists.
 * `transfers` had `created_at` and `updated_at` only, and `updated_at` is bumped by any
 * write, so no honest duration could be derived. VERA-260 added `transfers.completed_at`
 * (migration 0095) with a single write-once stamp at completion, and this card now reads
 * a real median. Its title lost the word "Avg" because the figure is a MEDIAN; its id
 * stays `log_avg_transfer_time` for the same reason `mkt_geo_reach` keeps its stale id —
 * ids are persisted inside saved views and renaming one drops the card from every view
 * that already references it.
 *
 * The remaining placeholders (Authenticity Verification Rate, Counterfeit Deflections,
 * Transfer Completion Rate, Certificate Coverage, In-Transit Rate, Scan Location
 * Accuracy, Fulfillment Visibility) are each still blocked on one identifiable thing and
 * are tracked rather than deleted.
 */

/**
 * ── EVERY CARD CARRIES A `term` (VERA-260) ──────────────────────────────────────────────
 *
 * `AnalyticsBlock`'s own comment used to note that "none of the 22 ROI cards carries a
 * `term`". All of them do now, and the reason each one earns an entry rather than leaning
 * on its `description` is that the two answer different questions and only one of them has
 * a single source (§44/§59):
 *
 *   description   ONE line, on this card, restating the title. Retyped here, and nowhere
 *                 else — so it can drift from the glossary, from Ask Vera's retrieval
 *                 context, and from the same figure's own definition, with nothing
 *                 comparing them.
 *   term          the glossary entry, which the tooltip BODY and the ⌘K palette and Ask
 *                 all read from one string. It is also where a caveat that does not fit on
 *                 a card face can live: that the two transfer counts are scoped by START
 *                 date, that the invalid rate's denominator is the scan record and not the
 *                 action log, that the map's repeat rate deliberately will not reconcile
 *                 with this page's.
 *
 * They COMPOSE rather than compete — `AnalyticsBlock` passes `description` as the tooltip's
 * body and still footers through to the full definition — so nothing was removed to add
 * these. Where the two disagree, the glossary is the one to fix (§44).
 *
 * ⚠️ AUTHORED, NEVER INFERRED, for the same reason `metricId` is. Glossary ids are a FOURTH
 * id space on this page, disjoint from the card ids, the KPI keys and `METRIC_REGISTRY` —
 * and a `term` that fails to resolve renders NO tooltip at all rather than a broken one,
 * because a term-only tooltip has no literal text to fall back to. A written-down id is
 * caught by `glossary-call-sites.guard.spec.ts`; a computed one would be invisible to it.
 */

export type ROITabKey = 'marketing' | 'legal' | 'logistics'

export const ALL_ROI_CARDS: Record<ROITabKey, ROICardDef[]> = {
  marketing: [
    /*
      REMOVED (VERA-260): `mkt_scan_engagement_rate` "Scan Engagement Rate".

      "Percentage of product units that received at least one consumer scan" needs a
      UNITS-PRODUCED denominator, and Vera does not hold one anywhere. A certificate is
      not a unit produced — it is a unit Vera was told about — so certificates-with-a-scan
      over certificates would answer a different question under this label, and every
      other candidate denominator has the same defect.

      This is the SAME class as the already-deleted `mkt_campaign_roi`: not an
      unimplemented query but an absent input, so no amount of backend work resolves it.
      It had been an em-dash since it shipped and would have stayed one. The earlier note
      below kept it on the grounds that it was "blocked on one identifiable missing
      field"; that test was applied wrongly here, because the missing field is not one
      Vera is ever going to hold — a brand's production run happens outside this system.
    */
    {
      id: 'mkt_repeat_scan_rate',
      term: 'repeat-scan-rate',
      title: 'Repeat Scan Rate',
      value: null,
      unit: '% of scanned items',
      description: 'Share of scanned items that were scanned more than once.',
    },
    {
      // ⚠️ ID IS DELIBERATELY STALE RELATIVE TO THE TITLE. The card is now "Countries
      // reached"; the id stays `mkt_geo_reach` because ids are persisted inside saved
      // views, and renaming one does not migrate a stored view — it silently drops the
      // card from every view that already referenced it. A title is display copy; an id
      // is a key. Do not "tidy" this.
      id: 'mkt_geo_reach',
      term: 'countries-reached',
      title: 'Countries reached',
      value: null,
      unit: 'countries',
      description: 'Number of distinct countries where scans were recorded, derived from scans with location data.',
    },
    /*
      REMOVED (VERA-260): `mkt_consumer_touchpoints` "Consumer Touchpoints".

      Its own description was "Total number of scans recorded across the selected
      period" — the Scans KPI tile, verbatim, under a marketing-sounding name. Two
      figures that are the same measurement sat on one page under two labels, so a
      reader comparing them could only conclude they were different things. Removing
      the duplicate is strictly better than reconciling it: the tile is the one with a
      dated history behind it, so it is also the one that can be charted.

      REMOVED (VERA-260): `mkt_campaign_roi` "Campaign ROI".

      A placeholder that could never resolve. It needs marketing spend, which Vera does
      not hold anywhere — not an unimplemented query, an absent input. It has been an
      em-dash since it shipped and would have stayed one.
    */
    {
      id: 'mkt_invalid_scans',
      term: 'invalid-scans',
      title: 'Invalid scans',
      value: null,
      unit: 'rejected scans',
      description:
        'Scans Vera rejected as invalid — a failed cryptographic proof, a replayed counter, or a tag it does not recognise.',
    },
    {
      id: 'mkt_invalid_scan_rate',
      term: 'invalid-scan-rate',
      title: 'Invalid scan rate',
      value: null,
      unit: '% of scans',
      description:
        'Share of all scans that were rejected as invalid. Numerator and denominator are counted from the same source, so the rate is a property of one population rather than a ratio between two.',
    },
    {
      id: 'mkt_leads_captured',
      term: 'leads-captured',
      title: 'Leads captured',
      value: null,
      unit: 'distinct emails',
      description:
        'Distinct consumer emails captured by scans in the period, deduplicated the same way the Leads list deduplicates them.',
    },
    {
      id: 'mkt_time_to_first_scan',
      term: 'time-to-first-scan',
      title: 'Time to first scan',
      value: null,
      unit: 'median days',
      description:
        'Typical gap between a certificate being created and its first scan. Median rather than average, so one long-dormant certificate cannot drag the figure past every real observation.',
    },
  ],
  legal: [
    {
      id: 'legal_authenticity_rate',
      term: 'authenticity-verification-rate',
      title: 'Authenticity Verification Rate',
      value: null,
      unit: '% of scans verified authentic',
      description: 'Share of scans that confirmed the product as genuine via Vera certificate.',
    },
    {
      id: 'legal_counterfeit_deflections',
      term: 'counterfeit-deflections',
      title: 'Counterfeit Deflections',
      value: null,
      unit: 'estimated incidents',
      description: 'Estimated number of counterfeit-product encounters caught via verification failure.',
    },
    {
      id: 'legal_transfer_completion_rate',
      term: 'transfer-completion-rate',
      title: 'Transfer Completion Rate',
      value: null,
      unit: '% of initiated transfers',
      description: 'Percentage of ownership transfer requests that completed successfully.',
    },
    {
      id: 'legal_certificate_coverage',
      term: 'certificate-coverage',
      title: 'Certificate Coverage',
      value: null,
      unit: '% of products certified',
      description: 'Proportion of products in the catalog that carry an active Vera certificate.',
    },
    // VRG-459 — the one input-driven metric that actually resolves. Placed here
    // because its registry `roles` include Legal. See `METRIC_CARDS` for why its two
    // siblings are deliberately absent.
    ...metricCard('return_fraud_value_recovered'),
    {
      id: 'legal_stolen_reports',
      term: 'reported-stolen',
      title: 'Reported stolen',
      value: null,
      unit: 'reports in period',
      description:
        'Certificates reported stolen during the selected period. Counts the dated report event, so it moves with the date filter — not the number currently in a stolen state.',
    },
    {
      id: 'legal_lost_reports',
      term: 'reported-lost',
      title: 'Reported lost',
      value: null,
      unit: 'reports in period',
      description:
        'Certificates reported lost during the selected period. Counts the dated report event, so it moves with the date filter — not the number currently in a lost state.',
    },
  ],
  logistics: [
    {
      // ⚠️ ID IS DELIBERATELY STALE RELATIVE TO THE TITLE, exactly as `mkt_geo_reach` is.
      // The figure is a MEDIAN, so the title no longer says "Avg" — but ids are persisted
      // inside saved views, and renaming one silently drops the card from every view that
      // already references it. A title is display copy; an id is a key. Do not "tidy" it.
      id: 'log_avg_transfer_time',
      term: 'transfer-completion-time',
      title: 'Transfer completion time',
      value: null,
      unit: 'median days',
      description:
        'Typical time from a transfer being started to it completing. Median rather than average, so one transfer a buyer left sitting for a month cannot drag the figure past every real observation.',
    },
    {
      id: 'log_time_to_first_resale',
      term: 'time-to-first-resale',
      title: 'Time to first resale',
      value: null,
      unit: 'median days from certificate creation',
      description:
        'Typical gap between a certificate being created and the first time that item is resold. Measured from creation, so it includes the time the item sat before its first sale. Items never resold are left out rather than counted as a long wait.',
    },
    /*
      NOT ADDED (VERA-260): "Product value retention" — resale price over original sale
      price. Scoped, investigated, and deliberately not shipped in any form.

      TODO(VRG-491-App.Sell.Capture.First.Sale.Price / VERA-632-App.Sell)

      The NUMERATOR is fine: `transfers.price` for `type='sell'`, and App.Sell adds
      `transfer_escrow.amount_minor` beside it. THE DENOMINATOR DOES NOT EXIST. Audited
      2026-08-30 across `main` and the App.Sell branch — there is no column anywhere
      recording what a brand originally sold an item for:

        products.price                  a SKU-level "from" price, and rewritten by every
                                        Shopify re-sync, so it rewrites history too.
        transfers.price on 'primary'    semantically right, NULL in production — the
                                        primary-transfer use-case never sets it, so only
                                        the QA seed has a value. It would look correct
                                        locally and be empty for real.
        orders.total                    order-level, no certificate/product link, and
                                        there is no order_items table at all.

      A ratio against `products.price` would render a clean percentage that is not the
      thing it claims to measure — one item's resale over a representative price for its
      whole SKU — which is worse than the em-dash it replaces, because nothing on screen
      would distinguish it from a real figure. Shipping it as a permanent placeholder is
      also out: this epic's whole test is that a card which cannot resolve gets deleted
      rather than parked on the page.

      So it is neither a card nor a placeholder until a first-sale price is captured.
      That is a data-capture change in the SELL path, not an analytics one.
    */
    {
      id: 'log_product_lifespan',
      term: 'product-lifespan',
      title: 'Product lifespan',
      value: null,
      unit: 'median days observed',
      /*
        THREE SENTENCES, and the previous version's other four were cut rather than reworded
        (VRG-517). It ran to ~90 words and spent most of them restating the basis line printed
        directly under the figure — "measured from its manufacture date where it has one…", "the
        line under the figure says how many fall on each side" — so a reader who hovered was told
        again, at length, what they had just read on the card face.

        What survives is only what CHANGES HOW THE NUMBER IS READ: it is not durability, and it is
        right-censored. Everything else is either on the face (the per-basis split, which
        `useRoiCards` puts in `unit`) or in the glossary entry the tooltip's footer links to.

        ⚠️ This does NOT go in the glossary `short`. It is 228 characters against that field's
        180-char cap, and the cap is load-bearing — `short` IS the tooltip body for every term
        that has no local override (§44). A card carrying local phrasing beside the definition
        link is the case `AnalyticsBlock`'s `term` prop documents, not a drift from it: the
        glossary answers "what is product lifespan", this answers "how do I read THIS figure".
      */
      description:
        'How long items keep being scanned — not how long they last. Vera never learns an item was discarded, so anything still in use keeps adding to this. Measured from manufacture date where known, otherwise from certificate creation.',
    },
    {
      id: 'log_in_transit_rate',
      term: 'in-transit-rate',
      title: 'In-Transit Rate',
      value: null,
      unit: '% of certificates',
      description: 'Share of certificates currently in an in-progress transfer state.',
    },
    {
      id: 'log_scan_location_accuracy',
      term: 'scan-location-accuracy',
      title: 'Scan Location Accuracy',
      value: null,
      unit: '% geo-resolved scans',
      description: 'Percentage of scans where geographic location was successfully resolved.',
    },
    {
      id: 'log_fulfillment_visibility',
      term: 'fulfillment-visibility',
      title: 'Fulfillment Visibility',
      value: null,
      unit: 'scan events per unit',
      description: 'Average number of scan events recorded per certificate across its lifecycle.',
    },
    {
      id: 'log_items_per_owner',
      term: 'items-per-owner',
      title: 'Items per owner',
      value: null,
      unit: 'certificates per owner',
      description:
        'Average number of certificates held per owner. Counts items, not SKUs — a collector holding six identical watches holds six items.',
    },
    {
      id: 'log_resales',
      term: 'resales',
      title: 'Resales',
      value: null,
      unit: 'completed resales',
      description:
        'Ownership transfers completed as a sale in the period. Gifts and the brand’s first handoff to an owner are excluded.',
    },
    {
      id: 'log_avg_resale_price',
      term: 'average-resale-price',
      title: 'Average resale price',
      value: null,
      unit: 'per priced resale',
      description:
        'Average declared price of a completed resale. Resales with no declared price are left out of the average rather than counted as zero.',
    },
    {
      id: 'log_resales_per_item',
      term: 'resales-per-resold-item',
      title: 'Resales per resold item',
      value: null,
      unit: 'resales per item',
      description:
        'How many times a resold item changes hands on average. Items that have never been resold are excluded, so this measures resale behaviour rather than catalogue size.',
    },
  ],
}

/** Default card IDs shown on first load (subset of ALL_ROI_CARDS per tab). */
export const DEFAULT_CARD_IDS: Record<ROITabKey, string[]> = {
  /*
    Marketing defaults to cards that carry REAL values today. Leading with a placeholder
    (Scan Engagement Rate needs a units-produced figure the system doesn't hold) made the
    tab read as broken on first load. It stays one click away in Customize.

    ⚠️ `mkt_invalid_scans` REPLACES the removed `mkt_consumer_touchpoints` in this slot,
    and it is a replacement in position rather than in kind — deliberately.

    Consumer Touchpoints was removed for being the Scans KPI tile under a second name, so
    the slot could not be filled by anything that restates a total already on the page.
    Invalid scans is the opposite: it is the only figure here a brand cannot see anywhere
    else in the product, it is computed rather than placeholdered, and it is the one that
    prompts an action when it moves. A default view earns its place by showing what a
    reader would not otherwise go looking for.

    Its rate is NOT defaulted alongside it. Two cards deriving from one measurement is how
    the duplication being removed here started, and the count is the more legible of the
    two on a first load — a rate needs its denominator read before it means anything. The
    rate stays one click away in Customize.
  */
  marketing: ['mkt_invalid_scans', 'mkt_repeat_scan_rate', 'mkt_geo_reach'],
  /*
    VRG-459 — `return_fraud_value_recovered` is DEFAULTED and the other two input-driven
    cards are not, and the split is on the same rule the Marketing note above states.

    A placeholder in a default view reads as a broken page. A card offering
    `1 input needed` does not: it is actionable, and supplying that one figure renders
    a real number that counts toward the hero, because this is the one input-driven
    metric with a resolver. `counterfeit_deflection_value` and
    `new_customer_acquisition_value` would take the input and still render an em-dash
    (`not_computed`), so they stay one click away in Customize until their measured
    halves exist — defaulting them would ship exactly the "entered a number, nothing
    changed" experience.
  */
  legal: [
    'legal_authenticity_rate',
    'return_fraud_value_recovered',
    'legal_transfer_completion_rate',
    'legal_certificate_coverage',
  ],
  /*
    VERA-260 — logistics now defaults to THREE REAL CARDS. It previously defaulted to
    `log_avg_transfer_time`, `log_in_transit_rate` and `log_scan_location_accuracy`, of
    which all three were permanent em-dashes: the tab's first load was three blanks,
    which reads as a broken page rather than as an empty dataset. That is the same
    failure the Marketing note above describes, and it went unfixed only because there
    was nothing real to put there.

    There is now. `log_avg_transfer_time` became computable when `transfers.completed_at`
    landed, and time-to-first-resale and product lifespan are new alongside it. The two
    surviving placeholders stay one click away in Customize.

    ⚠️ Two of the three will read `—` on a brand with no completed transfers or no
    resales — a genuinely empty dataset rather than an unimplementable metric, which is
    the distinction the whole tab is being sorted on.

    Product lifespan USED to be the third such case, and it was the worst one: nothing
    populates `manufactured_at` (Shopify hardcodes it null), so it was not "empty until
    the brand fills the data in", it was empty permanently. VRG-517 gave it a second
    origin — earliest certificate creation — so it now renders for any product that has
    been scanned. It reads `—` only when nothing was scanned in the period. The cost is
    that the median can span two different measurements, which is why its basis line
    states the split; see the `log_product_lifespan` case in `useRoiCards`.
    Transfer completion time in particular fills in only as transfers complete AFTER
    migration 0095, because historical rows were deliberately not backfilled; the card
    reports its own basis so that ramp is visible rather than mistaken for a bug.
  */
  logistics: [
    'log_avg_transfer_time',
    'log_time_to_first_resale',
    'log_product_lifespan',
  ],
}
